import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, mergeProps as _mergeProps, unref as _unref, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = { class: "mt-5" }
const _hoisted_2 = ["onScrollPassive"]
const _hoisted_3 = {
  key: 0,
  class: "d-flex justify-center my-2"
}

import { ref, onMounted, nextTick, watch, computed } from 'vue';
import draggable from 'vuedraggable';
import { useOpportunitiesStore } from '@/store/opportunities';
import { configRegistry } from '@/lib/configRegistry';
import { useFloatFormStore } from '@/store/floatForm';
import type { FormField } from '@/types/form';
import type { Opportunity } from '@/types/Opportunity';
import { useLayoutStore } from '@/store/layout';

const ITEMS_PER_PAGE = 10;


export default /*@__PURE__*/_defineComponent({
  __name: 'KanbanView',
  props: {
    title: {},
    moduleName: {},
    data: {},
    config: {},
    onUpdate: { type: Function },
    onRecordUpdate: { type: Function },
    statusField: {}
  },
  setup(__props: any) {

const props = __props;

const layoutStore = useLayoutStore();
const filters = computed(() => layoutStore.getLayout(props.moduleName)?.filters || {});

const scrollWrapper = ref<HTMLElement | null>(null);
const showLeft = ref(false);
const showRight = ref(true);
const columns = ref<any[]>([]);
const opportunitiesStore = useOpportunitiesStore();
const floatFormStore = useFloatFormStore();

const kanbanConfig = ref({
  confirmForm: false,
  statusField: props.statusField || 'status',
  changeFormFields: [] as FormField[],
});

const buildPagination = (page: number, filters: Record<string, any>) => {
  const searchParams = new URLSearchParams();

  searchParams.append('itemsPerPage', ITEMS_PER_PAGE.toString());
  searchParams.append('page', page.toString());

  for (const [key, val] of Object.entries(filters)) {
    if (val !== null && val !== '') {
      if (typeof val === 'object' && val.id) {
        searchParams.append(`filter[${key}]`, val.id); // Soporta objetos con id
      } else {
        searchParams.append(`filter[${key}]`, val);
      }
    }
  }

  return `&${searchParams.toString()}`;
};

const loadKanbanConfig = async () => {
  if (!props.moduleName) return;
  const loader = configRegistry[props.moduleName];
  if (!loader) return;

  const mod = await loader();
  if (mod?.KANBAN) {
    kanbanConfig.value = {
      confirmForm: !!mod.KANBAN.confirmForm,
      statusField: mod.KANBAN.statusField || 'status',
      changeFormFields: mod.KANBAN.changeFormFields || [],
    };
  }
};

const loadOpportunities = async () => {
  if (!props.data || props.data.length === 0) return;

  const activeFilters = filters.value;

  const loadedColumns = await Promise.all(
    props.data.map(async (status: any) => {
      const pagination = buildPagination(1, { ...activeFilters });
      const opportunities = await opportunitiesStore.fetchOpportunityByStatusName(status.name, pagination);
      return {
        id: status.id,
        name: status.name,
        tasks: opportunities || [],
        page: 1,
        hasMore: (opportunities?.length || 0) === ITEMS_PER_PAGE,
        loading: false,
      };
    })
  );

  columns.value = loadedColumns;
};

const onColumnScroll = async (column: any, event: Event) => {
  const el = event.target as HTMLElement;
  if (!el || column.loading || !column.hasMore) return;

  const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 10;
  if (!atBottom) return;

  column.loading = true;
  column.page += 1;

  try {
    const activeFilters = filters.value;
    const pagination = buildPagination(column.page, { ...activeFilters, status: column.name });

    const moreTasks = await opportunitiesStore.fetchOpportunityByStatusName(column.name, pagination);
    if (moreTasks && moreTasks.length > 0) {
      column.tasks.push(...moreTasks);
      column.hasMore = moreTasks.length === ITEMS_PER_PAGE;
    } else {
      column.hasMore = false;
    }
  } catch (e) {
    console.error('Error al cargar más tareas:', e);
    column.hasMore = false;
  } finally {
    column.loading = false;
  }
};


const handleTaskMoved = async (evt: any) => {
  const movedTask = evt.item?._underlying_vm_;
  if (!movedTask || !props.onRecordUpdate) return;

  const fromContainer = evt.from;
  const toContainer = evt.to;

  const sourceColumn = columns.value.find(col =>
    fromContainer?.closest('.v-card')?.querySelector('.v-card-title')?.textContent?.trim() === col.name
  );

  const destinationColumn = columns.value.find(col =>
    toContainer?.closest('.v-card')?.querySelector('.v-card-title')?.textContent?.trim() === col.name
  );

  if (!destinationColumn || !sourceColumn) return;

  const statusField = kanbanConfig.value.statusField;
  const newStatus = destinationColumn.name;
  const oldStatus = sourceColumn.name;

  if (oldStatus === newStatus) return;

  let payload: Record<string, any> = { [statusField]: newStatus };

  try {
    if (kanbanConfig.value.confirmForm && kanbanConfig.value.changeFormFields.length) {
      const extraData = await floatFormStore.openFormWithPromise({
        component: 'ChangeKanbanStatusForm',
        formTitle: 'Confirmar cambio de estado',
        formWidth: 700,
        props: { fields: kanbanConfig.value.changeFormFields },
      });
      payload = { ...payload, ...extraData };
    }

    await props.onRecordUpdate(movedTask.id, payload);
  } catch (e) {
    destinationColumn.tasks = destinationColumn.tasks.filter((t: Opportunity) => t.id !== movedTask.id);
    sourceColumn.tasks.splice(evt.oldIndex, 0, movedTask);
  }
};

const handleScroll = () => {
  const el = scrollWrapper.value;
  if (!el) return;

  const hasData = columns.value.length > 0;

  showLeft.value = el.scrollLeft > 0;

  if (hasData) {
    showRight.value = el.scrollLeft + el.clientWidth < el.scrollWidth;
  } else {
    // Si no hay datos aún, mantenerlo visible
    showRight.value = true;
  }
};

const scrollLeft = () => {
  scrollWrapper.value?.scrollBy({ left: -1500, behavior: 'smooth' });
};

const scrollRight = () => {
  scrollWrapper.value?.scrollBy({ left: 1500, behavior: 'smooth' });
};

onMounted(async () => {
  await loadKanbanConfig();
  await loadOpportunities();

  nextTick(() => {
    handleScroll();
    window.addEventListener('resize', handleScroll);
    scrollWrapper.value?.addEventListener('wheel', (e) => {
      if (e.shiftKey) {
        e.preventDefault();
        scrollWrapper.value!.scrollLeft += e.deltaY;
      }
    });
  });
});

watch(() => props.data, loadOpportunities);

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_divider = _resolveComponent("v-divider")!
  const _component_v_card_subtitle = _resolveComponent("v-card-subtitle")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_img = _resolveComponent("v-img")!
  const _component_v_avatar = _resolveComponent("v-avatar")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (showLeft.value)
      ? (_openBlock(), _createBlock(_component_v_btn, {
          key: 0,
          icon: "",
          onClick: scrollLeft,
          class: "position-fixed scroll-icons"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_v_icon, null, {
              default: _withCtx(() => _cache[1] || (_cache[1] = [
                _createTextVNode("mdi-chevron-left")
              ])),
              _: 1
            })
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    _createElementVNode("div", {
      ref_key: "scrollWrapper",
      ref: scrollWrapper,
      class: "d-flex overflow-x-auto flex-nowrap w-100 kanban-scroll",
      onScroll: handleScroll
    }, [
      _createVNode(_unref(draggable), {
        modelValue: columns.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((columns).value = $event)),
        group: "columns",
        "item-key": "id",
        tag: "div",
        class: "d-flex kanban-draggable",
        disabled: true
      }, {
        item: _withCtx(({ element: column }) => [
          _createVNode(_component_v_card, {
            class: "mr-2",
            width: "300",
            style: {"background":"transparent"}
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_card_title, { class: "status-title" }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(column.name), 1)
                ]),
                _: 2
              }, 1024),
              _createVNode(_component_v_divider),
              _createElementVNode("div", {
                class: "kanban-tasks",
                ref: "taskWrappers",
                onScrollPassive: ($event: any) => (onColumnScroll(column, $event))
              }, [
                _createVNode(_unref(draggable), {
                  modelValue: column.tasks,
                  "onUpdate:modelValue": ($event: any) => ((column.tasks) = $event),
                  group: "tasks",
                  "item-key": "id",
                  tag: "div",
                  onEnd: handleTaskMoved,
                  animation: 200,
                  "ghost-class": 'ghost'
                }, {
                  item: _withCtx(({ element: task }) => [
                    _createVNode(_component_v_card, {
                      class: "mx-1 my-2 px-3 py-2",
                      onClick: ($event: any) => (_ctx.$emit('viewItem', task.id))
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_card_subtitle, { class: "px-0" }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(task.opportunity_name), 1)
                          ]),
                          _: 2
                        }, 1024),
                        _createVNode(_component_v_card_subtitle, {
                          class: "text-caption px-0 mb-1",
                          style: {"transform":"scale(0.8) translateX(-30px) translateY(3px)"}
                        }, {
                          default: _withCtx(() => [
                            (task?.subject?.type=='clients')
                              ? (_openBlock(), _createBlock(_component_v_icon, {
                                  key: 0,
                                  size: "x-small",
                                  class: "mr-1"
                                }, {
                                  default: _withCtx(() => _cache[2] || (_cache[2] = [
                                    _createTextVNode("mdi-office-building-outline")
                                  ])),
                                  _: 1
                                }))
                              : _createCommentVNode("", true),
                            (task?.subject?.type=='leads')
                              ? (_openBlock(), _createBlock(_component_v_icon, {
                                  key: 1,
                                  size: "x-small",
                                  class: "mr-1"
                                }, {
                                  default: _withCtx(() => _cache[3] || (_cache[3] = [
                                    _createTextVNode("mdi-account-outline")
                                  ])),
                                  _: 1
                                }))
                              : _createCommentVNode("", true),
                            _createTextVNode(" " + _toDisplayString(task?.subject?.attributes?.legal_name || task?.subject?.attributes?.commercial_name || task?.subject?.attributes?.name), 1)
                          ]),
                          _: 2
                        }, 1024),
                        _createVNode(_component_v_chip, {
                          color: task?.subject?.type=='leads' ? '#b262ff' : '#6780ff',
                          label: "",
                          size: "x-small",
                          style: {"transform":"scale(0.7)","margin-left":"-10px"}
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(task?.subject?.type === 'clients' ? 'Cliente' : task?.subject?.type === 'leads' ? 'Lead' : ''), 1)
                          ]),
                          _: 2
                        }, 1032, ["color"]),
                        _createVNode(_component_v_card_subtitle, { class: "text-caption px-0" }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(_ctx.$toCurrency(task.value)), 1)
                          ]),
                          _: 2
                        }, 1024),
                        _createVNode(_component_v_card_subtitle, {
                          style: {"transform":"scale(0.8) translateX(-30px)"},
                          class: "text-caption px-0"
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(task.date), 1)
                          ]),
                          _: 2
                        }, 1024),
                        _createVNode(_component_v_chip, _mergeProps(props, {
                          link: "",
                          pill: "",
                          size: "x-small"
                        }), {
                          default: _withCtx(() => [
                            _createVNode(_component_v_avatar, {
                              class: "pa-1",
                              start: ""
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_v_img, {
                                  style: {"border-radius":"50%!important"},
                                  src: "https://ionicframework.com/docs/img/demos/avatar.svg"
                                })
                              ]),
                              _: 1
                            }),
                            _createTextVNode(" " + _toDisplayString(task?.user?.name + (task?.user?.last_name ? ' ' + task?.user?.last_name : '')), 1)
                          ]),
                          _: 2
                        }, 1040)
                      ]),
                      _: 2
                    }, 1032, ["onClick"])
                  ]),
                  _: 2
                }, 1032, ["modelValue", "onUpdate:modelValue"]),
                (column.loading)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                      _createVNode(_component_v_progress_circular, {
                        indeterminate: "",
                        size: "24"
                      })
                    ]))
                  : _createCommentVNode("", true)
              ], 40, _hoisted_2)
            ]),
            _: 2
          }, 1024)
        ]),
        _: 1
      }, 8, ["modelValue"])
    ], 544),
    (showRight.value)
      ? (_openBlock(), _createBlock(_component_v_btn, {
          key: 1,
          icon: "",
          onClick: scrollRight,
          class: "position-fixed scroll-icon-right scroll-icons"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_v_icon, null, {
              default: _withCtx(() => _cache[4] || (_cache[4] = [
                _createTextVNode("mdi-chevron-right")
              ])),
              _: 1
            })
          ]),
          _: 1
        }))
      : _createCommentVNode("", true)
  ]))
}
}

})